import { useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Tooltip } from 'react-tooltip';
import { fmt } from '../utils/helpers';

// URL do TopoJSON do mundo
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Mapeamento de nomes de países (do backend) para nomes do TopoJSON
const COUNTRY_NAME_MAPPING: { [key: string]: string } = {
    'Brazil': 'Brazil',
    'Chile': 'Chile',
    'Mexico': 'Mexico',
    'Poland': 'Poland',
    'Argentina': 'Argentina',
    'France': 'France',
    'United States': 'United States of America',
    'Peru': 'Peru',
    'Bolivia': 'Bolivia',
    'Ireland': 'Ireland',
    'Sweden': 'Sweden',
    'Bangladesh': 'Bangladesh',
    'Vietnam': 'Vietnam',
    'Colombia': 'Colombia',
    'Uruguay': 'Uruguay',
    'Paraguay': 'Paraguay',
    'Spain': 'Spain',
    'Portugal': 'Portugal',
    'Italy': 'Italy',
    'Germany': 'Germany',
    'United Kingdom': 'United Kingdom',
    'Canada': 'Canada',
    'Australia': 'Australia',
    'India': 'India',
    'China': 'China',
    'New Zealand': 'New Zealand',
    'Japan': 'Japan',
};

interface WorldMapProps {
    countries: any[];
    selectedCountry: any;
    onCountryClick: (country: any) => void;
    mapMetric: 'revenue' | 'clicks' | 'impressions';
}

export function WorldMap({ countries, selectedCountry, onCountryClick, mapMetric }: WorldMapProps) {
    const [tooltipContent, setTooltipContent] = useState('');

    // Função para obter o valor da métrica
    const getCountryMetricValue = (country: any) => {
        switch (mapMetric) {
            case 'revenue': return country.revenue;
            case 'clicks': return country.clicks;
            case 'impressions': return country.impressions;
            default: return country.revenue;
        }
    };

    // Calcular valores máximo e mínimo para normalização
    const metricValues = countries.map(c => getCountryMetricValue(c));
    const maxMetric = Math.max(...metricValues);
    const minMetric = Math.min(...metricValues.filter(v => v > 0));

    // Criar mapa de dados por nome de país (do TopoJSON)
    const countryDataMap = new Map();
    countries.forEach(country => {
        const topoJsonName = COUNTRY_NAME_MAPPING[country.country];
        if (topoJsonName) {
            countryDataMap.set(topoJsonName, country);
        }
    });

    // Função para calcular a cor baseada no valor
    const getCountryColor = (geoName: string) => {
        const countryData = countryDataMap.get(geoName);
        if (!countryData) return '#e5e7eb'; // cinza claro para países sem dados

        const value = getCountryMetricValue(countryData);
        if (value === 0) return '#e5e7eb';

        // Normalizar entre 0 e 1
        const normalized = (value - minMetric) / (maxMetric - minMetric);

        // Gradiente de verde baseado na métrica
        if (normalized > 0.7) return '#10b981'; // verde forte
        if (normalized > 0.5) return '#34d399'; // verde médio
        if (normalized > 0.3) return '#6ee7b7'; // verde claro
        if (normalized > 0.1) return '#a7f3d0'; // verde muito claro
        return '#d1fae5'; // verde quase branco
    };

    // Função para formatar o valor da métrica
    const formatMetricValue = (value: number) => {
        switch (mapMetric) {
            case 'revenue': return fmt.currency(value);
            case 'clicks': return fmt.number(value) + ' cliques';
            case 'impressions': return fmt.number(value) + ' impressões';
            default: return fmt.currency(value);
        }
    };

    return (
        <div className="relative">
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                    scale: 140,
                    center: [0, 20]
                }}
                className="w-full h-[500px]"
            >
                <ZoomableGroup zoom={1} center={[0, 20]}>
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const geoName = geo.properties.name;
                                const countryData = countryDataMap.get(geoName);
                                const isSelected = selectedCountry && COUNTRY_NAME_MAPPING[selectedCountry.country] === geoName;

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={isSelected ? '#3b82f6' : getCountryColor(geoName)}
                                        stroke="#fff"
                                        strokeWidth={0.5}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: {
                                                fill: countryData ? '#059669' : '#d1d5db',
                                                outline: 'none',
                                                cursor: countryData ? 'pointer' : 'default',
                                            },
                                            pressed: { outline: 'none' },
                                        }}
                                        onMouseEnter={() => {
                                            if (countryData) {
                                                const value = getCountryMetricValue(countryData);
                                                setTooltipContent(`${countryData.country}: ${formatMetricValue(value)}`);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            setTooltipContent('');
                                        }}
                                        onClick={() => {
                                            if (countryData) {
                                                onCountryClick(countryData);
                                            }
                                        }}
                                        data-tooltip-id="map-tooltip"
                                        data-tooltip-content={tooltipContent}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ZoomableGroup>
            </ComposableMap>

            <Tooltip id="map-tooltip" />

            {/* Legenda */}
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                    {mapMetric === 'revenue' && 'Receita'}
                    {mapMetric === 'clicks' && 'Cliques'}
                    {mapMetric === 'impressions' && 'Impressões'}
                </p>
                <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-[#d1fae5] border border-gray-300"></div>
                            <span className="text-xs text-gray-600">Baixo</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-[#6ee7b7] border border-gray-300"></div>
                            <span className="text-xs text-gray-600">Médio</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-[#10b981] border border-gray-300"></div>
                            <span className="text-xs text-gray-600">Alto</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-[#3b82f6] border border-gray-300"></div>
                            <span className="text-xs text-gray-600">Selecionado</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}